"use client";

import { authWithGoogle as authWithGoogleRequest } from "@vetty/api-sdk";
import { setGoogleAuthLoading } from "@/stores";
import {
  axiosInstance,
  buildMetaEventContext,
  getAdClickAttribution,
  pushAnalyticsError,
  pushDataLayerEvent,
  resolveSafeNextPath,
  trackMetaEvent,
  withLocale,
} from "@/helpers";
import { defaultLocale, isLocale } from "@/i18n";
import { toast } from "sonner";

const pathLocale = (): string => {
  if (typeof window === "undefined") return defaultLocale;
  const seg = window.location.pathname.split("/").filter(Boolean)[0];
  return isLocale(seg) ? seg : defaultLocale;
};

const authWithGoogle = async (code: string, isSignUp = false) => {
  const meta = buildMetaEventContext();
  const locale = pathLocale();
  const nextParam =
    typeof window !== "undefined"
      ? new URLSearchParams(window.location.search).get("next")
      : null;

  try {
    const { data } = await authWithGoogleRequest(axiosInstance, {
      code,
      attribution: getAdClickAttribution(),
      meta,
    });

    if (data.success) {
      const created = data.data?.created === true;

      if (created) {
        pushDataLayerEvent({
          event: "signup_success",
          method: "google",
        });
        trackMetaEvent(
          "CompleteRegistration",
          { content_name: "account_created", status: true },
          meta.eventId,
        );
      } else {
        pushDataLayerEvent({
          event: "login_success",
          method: "google",
        });
      }

      const { onboardingCompleted, role } = data.data;

      toast.success(
        "Successfully logged in using Google. A\u00A0redirect will take place in a moment.",
      );

      if (role === "demo" || onboardingCompleted) {
        window.location.href = resolveSafeNextPath(
          nextParam,
          withLocale(locale, "/"),
          locale,
        );
      } else {
        window.location.href = withLocale(locale, "/verification");
      }

      return;
    }
  } catch (error: unknown) {
    const body =
      error && typeof error === "object" && "response" in error
        ? (error as { response?: { data?: { code?: string; message?: string } } })
            .response?.data
        : undefined;
    pushAnalyticsError({
      event: isSignUp ? "signup_error" : "login_error",
      errorCode: String(body?.code || "oauth_error"),
      errorMessage: String(body?.message || "Google sign-in failed"),
      errorStep: "oauth",
      method: "google",
    });
    setGoogleAuthLoading(false);
  }
};

export { authWithGoogle };
